import PlayerRequestBuilder, { PreviewMode } from 'common/components/player/PlayerRequestBuilder';
import { WorkspacesOverlayTypes } from 'common/store/workspaces/types';
import i18nService from 'common/services/i18nService';
import { TAB_STORAGE_KEY, TabPaneTypes, TabStorageData } from '../types';
import store from '../../../store';
import { RootState } from '../../../store/rootReducer';

interface SubmissionService {
    getQuestionUrl: (
        assessmentRunId: number,
        showAnnotationSidebar: boolean,
        questionId: number,
        isStudent: boolean,
        partyId: number
    ) => string;
    getSubmissionUrl: (
        assessmentRunId: number,
        questionId: number,
        partyId: number,
        isStudent?: boolean,
        showAnnotationSidebar?: boolean
    ) => string;
    getSubmissionWithNavigation: (assessmentRunId: number, partyId: number) => string;
    getQuestionSetViewUrl: (assessmentRunId: number, partyId: number, contextMode: string, showAnnotationSidebar: boolean) => string;
    getDefaultSubmissionOverlayOptions: () => any;
    getLastActiveTab: () => string;
    getGradingWithNavigation: (assessmentRunId: number, partyId: number, studentView?: boolean) => string;
}


const submissionService: SubmissionService = {
    getGradingWithNavigation(assessmentRunId: number, partyId: number, studentView = false) {
        const { session } = store.getState() as RootState;
        // This function mimics getSubmissionWithNavigation, but uses context 'viewOwnDelivery' for marks_only grading
        const requestBuilder = new PlayerRequestBuilder();
        const languageId = i18nService.getCurrentLanguageId(session.language);
        const deliveryUrl = studentView ? 'viewOwnDelivery' : 'viewDelivery';
        requestBuilder
            .context(deliveryUrl)
            .assessmentRunId(assessmentRunId)
            .partyId(partyId);
        // If language selection is needed, add as query param manually
        let url = requestBuilder.toUrl();
        url += `&selectedLanguageId=${languageId}#/result`;
        return url;
    },
    getQuestionUrl(assessmentRunId: number, showAnnotationSidebar: boolean, questionId: number, isStudent: boolean, partyId: number) {
        const requestBuilder = new PlayerRequestBuilder();
        requestBuilder
            .assessmentRunId(assessmentRunId)
            .context('viewGradingQuestionPreview')
            .mode(PreviewMode.QUESTION_SET)
            .enableAnnotations(showAnnotationSidebar)
            .navigateToQuestion(questionId);

        if (!isStudent) {
            requestBuilder.partyId(partyId);
        }

        return requestBuilder.toUrl();
    },

    getSubmissionUrl(
        assessmentRunId: number,
        partyId: number,
        questionId: number,
        isStudent = false,
        showAnnotationSidebar = false
    ) {
        const requestBuilder = new PlayerRequestBuilder();
        requestBuilder
            .assessmentRunId(assessmentRunId)
            .partyId(partyId)
            .contentRevisionId(questionId)
            .enableAnnotations(showAnnotationSidebar)
            .mode(PreviewMode.QUESTION)
            .showAnnotatableResponses();

        if (isStudent) {
            requestBuilder.context('viewOwnDelivery');
        } else {
            requestBuilder.context('viewGradingQuestionDelivery');
        }

        return requestBuilder.toUrl();
    },

    getSubmissionWithNavigation(assessmentRunId: number, partyId: number) {
        const requestBuilder = new PlayerRequestBuilder();
        requestBuilder
            .context('viewDelivery')
            .assessmentRunId(assessmentRunId)
            .partyId(partyId);

        return requestBuilder.toUrl();
    },

    getQuestionSetViewUrl(assessmentRunId: number, partyId: number, contextMode: string, showAnnotationSidebar: boolean) {
        const requestBuilder = new PlayerRequestBuilder();
        requestBuilder
            .assessmentRunId(assessmentRunId)
            .context(contextMode)
            .mode(PreviewMode.QUESTION_SET)
            .enableAnnotations(showAnnotationSidebar)
            .partyId(partyId);

        return requestBuilder.toUrl();
    },

    getDefaultSubmissionOverlayOptions() {
        return {
            type: WorkspacesOverlayTypes.SUBMISSION,
            options: {
                fullHeight: true,
                withHeader: false,
                panes: [
                    TabPaneTypes.SUBMISSION,
                    TabPaneTypes.DRAWING,
                    TabPaneTypes.QUESTION_PREVIEW,
                    TabPaneTypes.ATTACHMENT,
                    TabPaneTypes.PLAGIARISM,
                    TabPaneTypes.MODEL_ANSWERS,
                ],
                showAnnotationSidebar: true,
            },
        };
    },

    getLastActiveTab(): string {
        const { context, session } = store.getState() as RootState;
        const { assessmentRunId, workspaceId } = context;
        const { id } = session?.user;
        const lastTab: TabStorageData = JSON.parse(localStorage.getItem(TAB_STORAGE_KEY));

        if (lastTab?.id === `${workspaceId}_${assessmentRunId}_${id}`) {
            return lastTab.tabId;
        }

        return undefined;
    },
};

export default submissionService;
